import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@components/core/IconButton';
import { DropdownProvider } from '../context/DropdownContext';
import Dropdown from '../core/Dropdown';
import classNames from 'classnames';
import Tooltip from '@components/core/Tooltip';
import { useTranslations } from '@components/context';
import StepForm from '../StepForm';
import FlowCard from '@components/composites/FlowCard';
import DragHandle, { DRAG_HANDLE_PLACEMENT } from '@components/core/DragHandle';
import Icon from '@components/core/Icon';
import { getStepVideoInterviewQuestionsCount } from '@components/PositionRecruitmentFlow/helper';
import ApplicationLockField, { isApplicationLockAvailable } from './ApplicationLockField';
import {
    FLOW_STEP_ICON,
    FLOW_STEP_LEGACY_CLASS,
    FLOW_STEP_LOCKED_STATUS,
    FLOW_STEP_VIDEO_TYPE,
} from './constants';

const LEGACY = FLOW_STEP_LEGACY_CLASS;

/** When set, StepForm opens beside the step name (recruitment flow editors). */
const FlowStep = ({
    metadata,
    step,
    readonly,
    onOpenVideoStepEditor,
    savedStepIds,
    children,
    className,
    allowDragAndDrop,
    dragHandleRef,
    dragging,
    dropTarget,
    body,
    onChange,
    onUpdateStep,
    onRemove,
    showRemove,
    isFirstStep,
}) => {
    const { id, name, default_name, type, i18n, status, locks_application = false } = step || {};

    const { step_types = {}, anonymous_fields = {}, can_choose_application_locking_step = false } = metadata;

    const { t } = useTranslations();
    const [showEditStepForm, setShowEditStepForm] = useState(false);

    const canOpenStepEditor = !readonly && status !== FLOW_STEP_LOCKED_STATUS;
    const stepType = step_types[type] || {};
    const icon = stepType.icon || FLOW_STEP_ICON.DEFAULT;
    const statusLabel = stepType.name || type || t('Status');
    const message = stepType.message;
    const infoBoxMessage = stepType.infobox_message;

    const alertHtml = message
        ? `${message} ${infoBoxMessage ? infoBox(infoBoxMessage) : ''}`
        : '';

    const videoQuestionCount = getStepVideoInterviewQuestionsCount(step);
    const isStepPersisted = savedStepIds?.has?.(String(id));
    const showVideoInterviewQuestions = Boolean(
        onOpenVideoStepEditor && type === FLOW_STEP_VIDEO_TYPE && isStepPersisted,
    );

    const onChangeAnonymousInfo = useCallback((newAnonymousInfo) => {
        !readonly && onChange({
            ...step,
            anonymous_info: { ...step.anonymous_info, ...newAnonymousInfo },
        });
    }, [step, onChange, readonly]);

    const onChangeApplicationLock = useCallback((locked) => {
        !readonly && onChange({ ...step, locks_application: locked });
    }, [step, onChange, readonly]);

    const openStepEditor = () => !readonly && setShowEditStepForm(true);

    const title = (
        <span className={classNames(LEGACY.NAME_BLOCK, { editable: !readonly })}>
            <span
                {...(canOpenStepEditor
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        className: classNames(LEGACY.NAME, 'pointer'),
                        'data-action': `${id}-edit_text`,
                        onClick: openStepEditor,
                        onKeyDown(e) {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openStepEditor();
                            }
                        },
                    }
                    : { className: LEGACY.NAME })}
            >
                {default_name || name}
            </span>
            {showEditStepForm && onUpdateStep ? (
                <StepForm
                    stepId={id}
                    name={default_name}
                    type={type}
                    i18n={i18n}
                    metadata={metadata}
                    onSubmit={(data) => {
                        onUpdateStep?.(data);
                        setShowEditStepForm(false);
                    }}
                    onClose={() => setShowEditStepForm(false)}
                />
            ) : null}
        </span>
    );

    const stepTypeLabel = (
        <span className={LEGACY.STATUS}>
            {statusLabel}
            {message ? (
                <div
                    className="ta-alert"
                    dangerouslySetInnerHTML={{ __html: alertHtml }}
                />
            ) : null}
        </span>
    );

    const meta = (
        <>
            {children}
            {showVideoInterviewQuestions ? (
                <a
                    className={classNames('btn btn-link', LEGACY.VIDEO_LINK)}
                    onClick={() => onOpenVideoStepEditor(id)}
                >
                    <span className={classNames('icon', FLOW_STEP_ICON.VIDEO)} /> {t('Edit questions')}
                    <span className="video-interview-questions-count badge badge-primary badge-rounded badge-sm">
                        {videoQuestionCount}
                    </span>
                </a>
            ) : null}
        </>
    );

    const actions = (
        <>
            <AnonymousRecruitmentField
                visible={!_.isEmpty(anonymous_fields)}
                fields={anonymous_fields}
                step={step}
                readonly={readonly}
                onChange={onChangeAnonymousInfo}
            />
            {isApplicationLockAvailable({ isFirstStep, stepType }) ? (
                <ApplicationLockField
                    locked={Boolean(locks_application)}
                    disabled={readonly || !can_choose_application_locking_step}
                    onChange={onChangeApplicationLock}
                />
            ) : null}
            {showRemove ? (
                <Tooltip title={t('Remove')}>
                    <IconButton
                        variant="plain"
                        icon={FLOW_STEP_ICON.REMOVE}
                        label={t('Remove')}
                        className={LEGACY.REMOVE}
                        data-action={`${id}-Remove`}
                        onClick={onRemove}
                    />
                </Tooltip>
            ) : null}
        </>
    );

    return (
        <FlowCard
            className={classNames(className, LEGACY.CARD, 'last-added')}
            data-id={id}
            dragging={dragging}
            dropTarget={dropTarget}
            body={body}
            icon={<Icon value={icon} noMargin />}
            title={title}
            type={stepTypeLabel}
            meta={meta}
            actions={actions}
            dragHandle={allowDragAndDrop ? (
                <DragHandle
                    ref={dragHandleRef}
                    label={t('Reorder step')}
                    placement={DRAG_HANDLE_PLACEMENT.OUTSIDE}
                    className={LEGACY.DRAG_HANDLE}
                />
            ) : null}
        />
    );
};

const AnonymousRecruitmentField = ({ visible, fields, step, readonly, onChange }) => {
    const { t } = useTranslations();
    const selectAllRef = useRef(null);
    const anonymousInfo = _.get(step, 'anonymous_info', {});
    const isEnabled = _.get(anonymousInfo, 'enable', false);

    const fieldIds = useMemo(() => _.keys(fields), [fields]);

    const selectedFields = useMemo(() => {
        const raw = _.get(anonymousInfo, 'fields', []);
        return raw.filter((id) => Object.prototype.hasOwnProperty.call(fields, id));
    }, [anonymousInfo, fields]);

    useEffect(() => {
        if (readonly || !visible) {
            return;
        }
        const raw = _.get(anonymousInfo, 'fields', []);
        if (raw.length > selectedFields.length) {
            onChange({ ...anonymousInfo, fields: selectedFields });
        }
    }, [step?.id, fieldIds.join(','), readonly, visible]);

    const updateAnonymousInfo = useCallback(
        (patch) => {
            if (!readonly) {
                onChange({ ...anonymousInfo, ...patch });
            }
        },
        [anonymousInfo, onChange, readonly],
    );

    const onFieldToggle = useCallback(
        (fieldId) => {
            const nextFields = selectedFields.includes(fieldId)
                ? selectedFields.filter((f) => f !== fieldId)
                : [...selectedFields, fieldId];
            updateAnonymousInfo({ fields: nextFields });
        },
        [selectedFields, updateAnonymousInfo],
    );

    const onEnableChange = useCallback(
        (enabling) => {
            const patch = { enable: enabling };
            if (enabling && selectedFields.length === 0) {
                patch.fields = fieldIds;
            }
            updateAnonymousInfo(patch);
        },
        [fieldIds, selectedFields.length, updateAnonymousInfo],
    );

    const isAllSelected =
        fieldIds.length > 0 && fieldIds.every((fid) => selectedFields.includes(fid));
    const isSomeSelected = selectedFields.length > 0 && !isAllSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = isSomeSelected;
        }
    }, [isSomeSelected, isAllSelected]);

    const onAllSelectHandler = useCallback(
        (e) => {
            updateAnonymousInfo({ fields: e.target.checked ? fieldIds : [] });
        },
        [fieldIds, updateAnonymousInfo],
    );

    if (!visible) return null;

    return (
        <div className={LEGACY.ANONYMOUS}>
            <input
                type="checkbox"
                name="anonymous_recruitment"
                checked={isEnabled}
                onChange={(e) => onEnableChange(e.target.checked)}
                style={{ display: 'none' }}
                tabIndex={-1}
                aria-hidden="true"
            />
            <div className="group-switch">
                <button
                    type="button"
                    className={classNames('toggle-button', 'toggle-switch-rounded', {
                        active: isEnabled,
                        readonly: readonly,
                    })}
                    aria-pressed={isEnabled}
                    aria-label={t('Anonymize')}
                    disabled={readonly}
                    onClick={() => onEnableChange(!isEnabled)}
                >
                    <Tooltip title={t('Anonymize')}>
                        <span className={classNames('icon', FLOW_STEP_ICON.ANONYMOUS)} />
                    </Tooltip>
                </button>
                <div className={classNames('action-container', { visible: isEnabled })}>
                    <DropdownProvider>
                        <Dropdown
                            id={`dropdown-${step.id}`}
                            btnClass="menu-btn"
                            icon={FLOW_STEP_ICON.ANONYMOUS_SETTINGS}
                            disabled={!isEnabled}
                        >
                            {() => (
                                <Fragment>
                                    <li className="header-item">
                                        <label>
                                            <input
                                                ref={selectAllRef}
                                                type="checkbox"
                                                checked={isAllSelected}
                                                disabled={readonly}
                                                onChange={onAllSelectHandler}
                                            />
                                            {t('Select all')}
                                        </label>
                                    </li>
                                    {_.map(fields, ({ title }, fid) => (
                                        <li key={fid}>
                                            <div>
                                                <label htmlFor={`${step.id}-${fid}`}>
                                                    <input
                                                        id={`${step.id}-${fid}`}
                                                        type="checkbox"
                                                        name="anonymous_field"
                                                        value={fid}
                                                        disabled={readonly}
                                                        checked={selectedFields.includes(fid)}
                                                        onChange={() => onFieldToggle(fid)}
                                                    />
                                                    {title}
                                                </label>
                                            </div>
                                        </li>
                                    ))}
                                </Fragment>
                            )}
                        </Dropdown>
                    </DropdownProvider>
                </div>
            </div>
        </div>
    );
};

export { FLOW_STEP_LEGACY_CLASS, FLOW_STEP_ICON };
export default FlowStep;
